// US-CHORE-01 -- show the chores a guardian has set up for a child (or
// the chores a signed-in child has been assigned).
// US-CHORE-02 -- child taps Mark done on a chore to claim it.
// US-CHORE-06 -- guardian deactivates / reactivates a chore.

import {
  type ChoreEntry,
  type MasterListChoreEntry,
  type RejectedRemovedChoreEntry,
  choreClaim,
  choreCompletionArchive,
  choreCompletionResubmit,
  choreDeactivate,
  chorePick,
  choreReactivate,
  choresList,
  choresListRejectedRemoved,
  inactiveChoresList,
  masterListChoresList,
} from '@chorepop/core/client';
import { type Component, For, Match, Show, Switch, createResource, createSignal } from 'solid-js';
import { AppIcon } from '../components/AppIcon';
import { ReportContentButton } from '../components/ReportContentButton';
import { TransientBanner } from '../components/TransientBanner';
import { burstChildConfetti } from '../lib/childCelebration';
import { confettiBurstsEnabled } from '../state/childCelebrationPreferences';
import { isFamilyContentHidden } from '../state/familyContentVisibility';

type Props = {
  sessionId: string;
  /** For guardians, which linked child. Undefined = all children (All Chores view). For children, their own id. */
  childAccountId: number | undefined;
  childDisplayName: string;
  currencyCode: string;
  /** Whether to show the "Add chore" affordance. Guardians only. */
  canCreate: boolean;
  /** Whether to show the Mark-done button. Children only, and only on their own chores. */
  canClaim: boolean;
  /** Whether to show deactivate/reactivate affordances + the Active/Deactivated
   *  view toggle. Guardians only. US-CHORE-06. */
  canDeactivate: boolean;
  /** Whether to show an Edit button on each active chore tile. Guardians only. */
  canEdit: boolean;
  onAddChore: () => void;
  /** Called when the guardian taps Edit on a chore tile. */
  onEditChore?: (chore: ChoreEntry) => void;
  onBack: () => void;
  /** The guardian's bottom-tab destination, rather than a pushed child view. */
  topLevel?: boolean;
};

type View = 'active' | 'deactivated';
/** US-CHORE-08 -- child-only sub-view. The "Available" tab surfaces
 *  master-list chores ("any child" pool) the calling child can pick;
 *  the "Assigned" tab is the existing chores list (chores already
 *  assigned to me). Guardians don't see the tabs. */
type ChildTab = 'assigned' | 'available';

export const ChoresScreen: Component<Props> = (props) => {
  const [view, setView] = createSignal<View>('active');
  const [childTab, setChildTab] = createSignal<ChildTab>('assigned');

  const [chores, { refetch }] = createResource(view, async (currentView) => {
    const call =
      currentView === 'active'
        ? choresList({
            sessionId: props.sessionId,
            childAccountId: props.childAccountId,
          })
        : inactiveChoresList({
            sessionId: props.sessionId,
            childAccountId: props.childAccountId,
          });
    const result = await call;
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  });

  const [claimingId, setClaimingId] = createSignal<number | null>(null);
  const [busyChoreId, setBusyChoreId] = createSignal<number | null>(null);
  const [flash, setFlash] = createSignal<string | null>(null);
  const [claimError, setClaimError] = createSignal<string | null>(null);
  // US-CHORE-10 -- child-only resources for the resolution surface.
  // The Re-submit / Archive flows live alongside the chore list, so
  // their busy state is tracked on completion id rather than chore id.
  const [resubmittingId, setResubmittingId] = createSignal<number | null>(null);
  const [archivingId, setArchivingId] = createSignal<number | null>(null);
  const [rejectedRemoved, { refetch: refetchRejectedRemoved }] = createResource(async () => {
    if (!props.canClaim) return [] as RejectedRemovedChoreEntry[];
    const r = await choresListRejectedRemoved({ sessionId: props.sessionId });
    if (!r.ok) return [] as RejectedRemovedChoreEntry[];
    return r.value;
  });

  // US-CHORE-08 -- master-list chores ("Available" tab). Only fetched
  // for child viewers; guardians never see this list (their flow is
  // the redesigned all-chores filtered view from US-CHORE-07's spec
  // note, not yet built).
  const [masterList, { refetch: refetchMasterList }] = createResource(async () => {
    if (!props.canClaim) return [] as MasterListChoreEntry[];
    const r = await masterListChoresList({ sessionId: props.sessionId });
    if (!r.ok) return [] as MasterListChoreEntry[];
    return r.value;
  });
  const [pickingId, setPickingId] = createSignal<number | null>(null);

  // US-CHORE-08 -- pick a master-list chore. Two outcomes:
  //   * 'assigned' -- chore now mine; switch to Assigned tab and refresh both.
  //   * 'pending'  -- guardian must approve; chore stays on master list with
  //                   "Waiting on approval" chip.
  const onPick = async (entry: MasterListChoreEntry) => {
    setPickingId(entry.choreId);
    setClaimError(null);
    try {
      const result = await chorePick({
        sessionId: props.sessionId,
        choreId: entry.choreId,
      });
      if (!result.ok) {
        setClaimError(
          result.error.kind === 'conflict'
            ? 'This chore was just picked up by someone else. Refreshing…'
            : result.error.message,
        );
        void refetchMasterList();
        return;
      }
      if (result.value.outcome === 'assigned') {
        setFlash(`Added "${entry.choreName}" to your chores!`);
        setChildTab('assigned');
        void refetch();
      } else {
        setFlash(`Waiting on a guardian to approve your pick of "${entry.choreName}".`);
      }
      void refetchMasterList();
    } finally {
      setPickingId(null);
    }
  };

  // US-CHORE-10 (10b) -- child re-submits a rejected chore. Uses the
  // rejected completion's id (carried on the row) so the call goes
  // straight through to the backend without having to re-discover it.
  const onResubmit = async (chore: ChoreEntry) => {
    if (chore.lastRejectedCompletionId === null) return;
    setResubmittingId(chore.lastRejectedCompletionId);
    setClaimError(null);
    try {
      const result = await choreCompletionResubmit({
        sessionId: props.sessionId,
        choreCompletionId: chore.lastRejectedCompletionId,
      });
      if (!result.ok) {
        setClaimError(
          result.error.kind === 'conflict'
            ? 'This chore is no longer rejected -- refresh to see its current state.'
            : result.error.message,
        );
        return;
      }
      setFlash(`Re-submitted "${chore.choreName}". Waiting on your guardian.`);
      void refetch();
    } finally {
      setResubmittingId(null);
    }
  };

  // US-CHORE-10 (10c) -- child archives a rejected-and-removed
  // completion (the chore was killed by the guardian via Reject+remove).
  // Sets `child_archived = 1`; the row drops out of the list on the
  // next refetch.
  const onArchive = async (entry: RejectedRemovedChoreEntry) => {
    setArchivingId(entry.choreCompletionId);
    setClaimError(null);
    try {
      const result = await choreCompletionArchive({
        sessionId: props.sessionId,
        choreCompletionId: entry.choreCompletionId,
      });
      if (!result.ok) {
        setClaimError(result.error.message);
        return;
      }
      setFlash(`Archived "${entry.choreName}".`);
      void refetchRejectedRemoved();
    } finally {
      setArchivingId(null);
    }
  };

  const onMarkDone = async (chore: ChoreEntry, origin?: DOMRectReadOnly) => {
    setClaimingId(chore.choreId);
    setClaimError(null);
    try {
      const result = await choreClaim({
        sessionId: props.sessionId,
        choreId: chore.choreId,
      });
      if (!result.ok) {
        setClaimError(
          result.error.kind === 'conflict'
            ? "You've already marked this one done -- wait for your guardian."
            : result.error.message,
        );
        return;
      }
      setFlash(`Marked "${chore.choreName}" as done. Waiting on your guardian.`);
      if (confettiBurstsEnabled()) burstChildConfetti(origin);
      void refetch();
    } finally {
      setClaimingId(null);
    }
  };

  const friendlyConflict = 'Already done -- refreshing.';

  const onDeactivate = async (chore: ChoreEntry) => {
    setBusyChoreId(chore.choreId);
    setClaimError(null);
    try {
      const result = await choreDeactivate({
        sessionId: props.sessionId,
        choreId: chore.choreId,
      });
      if (!result.ok) {
        setClaimError(result.error.kind === 'conflict' ? friendlyConflict : result.error.message);
        void refetch();
        return;
      }
      setFlash(`Deactivated "${chore.choreName}".`);
      void refetch();
    } finally {
      setBusyChoreId(null);
    }
  };

  const onReactivate = async (chore: ChoreEntry) => {
    setBusyChoreId(chore.choreId);
    setClaimError(null);
    try {
      const result = await choreReactivate({
        sessionId: props.sessionId,
        choreId: chore.choreId,
      });
      if (!result.ok) {
        setClaimError(result.error.kind === 'conflict' ? friendlyConflict : result.error.message);
        void refetch();
        return;
      }
      setFlash(`Reactivated "${chore.choreName}".`);
      void refetch();
    } finally {
      setBusyChoreId(null);
    }
  };

  return (
    <main class="history-screen history-screen--claude chore-screen">
      <header
        class={`history-header history-header--claude${props.topLevel ? ' chore-screen-header--tab' : ''}`}
      >
        <Show when={!props.topLevel}>
          <button type="button" class="login-back" onClick={() => props.onBack()}>
            ← Back
          </button>
        </Show>
        <div>
          <Show when={!props.topLevel}>
            <span class="action-screen-eyebrow">
              {props.canCreate ? 'Family chores' : 'Your chores'}
            </span>
          </Show>
          <h1>
            {props.childDisplayName === 'All Chores'
              ? 'Chores'
              : `${props.childDisplayName}'s chores`}
          </h1>
        </div>
      </header>

      <div class="chore-screen-toolbar">
        <Show when={props.canDeactivate}>
          <section class="chore-filter-row" aria-label="Chore status">
            <button
              type="button"
              class={view() === 'active' ? 'primary' : 'ghost'}
              onClick={() => {
                setView('active');
                setFlash(null);
                setClaimError(null);
              }}
            >
              Active
            </button>
            <button
              type="button"
              class={view() === 'deactivated' ? 'primary' : 'ghost'}
              onClick={() => {
                setView('deactivated');
                setFlash(null);
                setClaimError(null);
              }}
            >
              Deactivated
            </button>
          </section>
        </Show>

        <Show when={props.canCreate && view() === 'active'}>
          <section class="chore-create-card">
            <button type="button" onClick={() => props.onAddChore()}>
              <span aria-hidden="true">+</span> New chore
            </button>
          </section>
        </Show>

        {/* US-CHORE-08 -- child-only Available/Assigned tabs. Per the
          2026-04-26 redesign decision, the child's "My chores" entry
          point is a single screen with two tabs -- Available
          (master-list pool) and Assigned (chores already mine). The
          tabs render only for child viewers; guardians keep their
          existing single-list view. */}
        <Show when={props.canClaim}>
          <section class="chore-filter-row" aria-label="Chore assignment">
            <button
              type="button"
              class={childTab() === 'assigned' ? 'primary' : 'ghost'}
              onClick={() => {
                setChildTab('assigned');
                setFlash(null);
                setClaimError(null);
              }}
              data-testid="chores-tab-assigned"
            >
              Assigned chores
            </button>
            <button
              type="button"
              class={childTab() === 'available' ? 'primary' : 'ghost'}
              onClick={() => {
                setChildTab('available');
                setFlash(null);
                setClaimError(null);
              }}
              data-testid="chores-tab-available"
            >
              Available chores
              <Show when={(masterList()?.length ?? 0) > 0}>
                <span class="history-chip" style="margin-left: 6px;">
                  {masterList()?.length ?? 0}
                </span>
              </Show>
            </button>
          </section>
        </Show>
      </div>

      <div class="chore-screen-scroll">
        <Show when={flash()}>
          {(msg) => (
            <TransientBanner class="home-flash" message={msg()} onDismiss={() => setFlash(null)} />
          )}
        </Show>

        <Show when={claimError()}>
          {(msg) => (
            <div class="form-error" role="alert">
              {msg()}
            </div>
          )}
        </Show>

        {/* US-CHORE-08 -- Available tab body. Master-list chores with a
          Pick button (or "Pending approval" chip if this child
          already submitted a claim). Hidden on Assigned tab and for
          guardians. */}
        <Show when={props.canClaim && childTab() === 'available'}>
          <Switch>
            <Match when={masterList.loading}>
              <section class="home-card">
                <p>Loading available chores…</p>
              </section>
            </Match>
            <Match when={(masterList()?.length ?? 0) === 0}>
              <section class="home-card">
                <p style="margin: 0; color: var(--ink-2);">
                  Nothing on the master list right now. Check back later -- or ask your guardian to
                  add some chores anyone can pick up.
                </p>
              </section>
            </Match>
            <Match when={(masterList()?.length ?? 0) > 0}>
              <ul class="history-list chore-management-list">
                <For
                  each={(masterList() ?? []).filter(
                    (entry) => !isFamilyContentHidden(`chore:${entry.choreId}`),
                  )}
                >
                  {(entry) => (
                    <li
                      class="history-row history-row--deposit"
                      data-testid={`master-list-row-${entry.choreId}`}
                    >
                      <div class="history-row-leading">
                        <span class="history-amount">
                          +{(entry.rewardMinorUnits / 100).toFixed(2)} {props.currencyCode}
                        </span>
                      </div>
                      <div class="history-row-body">
                        <div class="history-row-title">
                          {entry.choreName}
                          <Show when={entry.requiresApprovalToClaim}>
                            <span
                              class="history-chip"
                              style="margin-left: 6px; background: #fff4cd; color: #8a6d00;"
                              title="Requires guardian approval before you can start"
                            >
                              Approval needed
                            </span>
                          </Show>
                        </div>
                        <Show when={entry.choreDescription}>
                          {(d) => (
                            <div class="history-row-allocations" style="color: var(--ink-2);">
                              {d()}
                            </div>
                          )}
                        </Show>
                        <div style="margin-top: 8px;">
                          <ReportContentButton
                            context={{
                              contentKind: 'chore',
                              contentLocalId: `chore:${entry.choreId}`,
                              label: `available chore “${entry.choreName}”`,
                              contentSnapshot: {
                                choreId: entry.choreId,
                                choreName: entry.choreName,
                                choreDescription: entry.choreDescription,
                                rewardMinorUnits: entry.rewardMinorUnits,
                                requiresApprovalToClaim: entry.requiresApprovalToClaim,
                              },
                            }}
                          />
                          <Show
                            when={!entry.hasPendingClaimFromMe}
                            fallback={
                              <span
                                class="history-chip"
                                style="background: #fff4cd; color: #8a6d00;"
                              >
                                Waiting on approval
                              </span>
                            }
                          >
                            <button
                              type="button"
                              class="bucket-tile-spend"
                              disabled={pickingId() === entry.choreId}
                              onClick={() => onPick(entry)}
                              data-testid={`master-list-row-${entry.choreId}-pick`}
                            >
                              {pickingId() === entry.choreId ? 'Picking…' : 'Pick'}
                            </button>
                          </Show>
                        </div>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </Match>
          </Switch>
        </Show>

        {/* Existing Assigned/active list. Hidden when child has the
          Available tab selected. Wrapped here in a Show so the tabs
          truly switch screens. */}
        <Show when={!props.canClaim || childTab() === 'assigned'}>
          <Switch>
            <Match when={chores.loading}>
              <section class="home-card">
                <p>Loading chores…</p>
              </section>
            </Match>

            <Match when={chores.error}>
              <section class="home-card">
                <p class="form-error" role="alert">
                  Couldn't load chores: {(chores.error as Error).message}
                </p>
              </section>
            </Match>

            <Match when={chores() && chores()?.length === 0}>
              <section class="home-card">
                <p>
                  <Switch>
                    <Match when={view() === 'deactivated'}>No deactivated chores.</Match>
                    <Match when={props.canCreate}>
                      No chores yet. Click "Add chore" to create the first one.
                    </Match>
                    <Match when={true}>No chores yet.</Match>
                  </Switch>
                </p>
              </section>
            </Match>

            <Match when={(chores()?.length ?? 0) > 0}>
              <ul class="history-list chore-management-list">
                <For
                  each={(chores() ?? []).filter(
                    (chore) => !isFamilyContentHidden(`chore:${chore.choreId}`),
                  )}
                >
                  {(chore) => (
                    <ChoreRow
                      chore={chore}
                      currencyCode={props.currencyCode}
                      canClaim={props.canClaim && view() === 'active'}
                      canDeactivate={props.canDeactivate && view() === 'active'}
                      canReactivate={props.canDeactivate && view() === 'deactivated'}
                      canEdit={props.canEdit && view() === 'active'}
                      showAssignee={props.canCreate && props.childAccountId === undefined}
                      busyClaiming={claimingId() === chore.choreId}
                      busyChore={busyChoreId() === chore.choreId}
                      busyResubmitting={
                        chore.lastRejectedCompletionId !== null &&
                        resubmittingId() === chore.lastRejectedCompletionId
                      }
                      onMarkDone={onMarkDone}
                      onResubmit={onResubmit}
                      onDeactivate={onDeactivate}
                      onReactivate={onReactivate}
                      onEdit={props.onEditChore ?? undefined}
                    />
                  )}
                </For>
              </ul>
            </Match>
          </Switch>
        </Show>

        {/* US-CHORE-10 (10c) -- rejected-and-removed chores. The
          underlying chore was killed by the guardian via Reject+remove,
          so it doesn't appear in the chores list above. The child sees
          a separate "Removed by your guardian" section here with an
          Archive button per row, plus the inline rejection note. Only
          rendered for child viewers (canClaim=true) and only when the
          list is non-empty. */}
        <Show when={props.canClaim && (rejectedRemoved()?.length ?? 0) > 0}>
          <section class="home-card" style="margin-top: var(--space-4);">
            <h2 style="margin: 0 0 8px;">Removed by your guardian</h2>
            <p style="margin: 0 0 12px; color: var(--ink-2); font-size: 0.9rem;">
              These chores were rejected and removed. You can archive them to clear them from this
              view -- your guardian will still see the history.
            </p>
            <ul class="history-list">
              <For each={rejectedRemoved()}>
                {(entry) => (
                  <li
                    class="history-row history-row--deposit chore-row-rejected"
                    data-testid={`rejected-removed-row-${entry.choreCompletionId}`}
                  >
                    <div class="history-row-leading">
                      <span class="history-amount" style="color: var(--ink-2);">
                        --
                      </span>
                    </div>
                    <div class="history-row-body">
                      <div class="history-row-title">{entry.choreName}</div>
                      <div class="history-row-allocations">
                        <span class="history-chip chore-row-rejected-chip">
                          Removed by your guardian
                        </span>
                      </div>
                      <Show when={entry.decisionReason}>
                        {(reason) => (
                          <div class="chore-row-rejected-reason">
                            <strong>Why:</strong> {reason()}
                          </div>
                        )}
                      </Show>
                      <div style="margin-top: 8px;">
                        <button
                          type="button"
                          class="ghost"
                          disabled={archivingId() === entry.choreCompletionId}
                          onClick={() => onArchive(entry)}
                          data-testid={`rejected-removed-row-${entry.choreCompletionId}-archive`}
                        >
                          {archivingId() === entry.choreCompletionId ? 'Archiving…' : 'Archive'}
                        </button>
                      </div>
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </section>
        </Show>
      </div>
    </main>
  );
};

const ChoreRow: Component<{
  chore: ChoreEntry;
  currencyCode: string;
  canClaim: boolean;
  canDeactivate: boolean;
  canReactivate: boolean;
  canEdit: boolean;
  showAssignee: boolean;
  busyClaiming: boolean;
  busyChore: boolean;
  /** US-CHORE-10 (10b): true while the resubmit call for THIS row is
   *  in flight. */
  busyResubmitting: boolean;
  onMarkDone: (chore: ChoreEntry, origin?: DOMRectReadOnly) => void;
  onResubmit: (chore: ChoreEntry) => void;
  onDeactivate: (chore: ChoreEntry) => void;
  onReactivate: (chore: ChoreEntry) => void;
  onEdit?: ((chore: ChoreEntry) => void) | undefined;
}> = (props) => {
  const formatReward = (minor: number) => {
    if (minor === 0) return 'No reward';
    const major = minor / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: props.currencyCode,
      }).format(major);
    } catch {
      return `${props.currencyCode} ${major.toFixed(2)}`;
    }
  };

  return (
    <li
      class={
        props.chore.lastRejectedAtUtc
          ? 'chore-management-card chore-row-rejected'
          : 'chore-management-card'
      }
    >
      <div class="chore-management-card-main">
        <AppIcon name="basket" class="chore-management-icon" />
        <div class="chore-management-copy">
          <div class="chore-management-title">{props.chore.choreName}</div>
          <div class="chore-management-meta">
            <span>{props.chore.choreTypeLabel}</span>
            <Show when={props.chore.zoneName}>{(zone) => <span>{zone()}</span>}</Show>
            <Show when={props.chore.recurrencePattern}>
              {(schedule) => <span>{schedule()}</span>}
            </Show>
            <Show when={props.chore.childAccountId === null}>
              <span class="chore-management-open">Open to anyone</span>
            </Show>
            <Show when={props.showAssignee && props.chore.childDisplayName}>
              {(name) => (
                <span
                  class="chore-management-assignee"
                  data-testid={`chore-${props.chore.choreId}-assignee`}
                >
                  For {name()}
                </span>
              )}
            </Show>
          </div>
        </div>
        <span
          class={`chore-management-reward${props.chore.rewardMinorUnits === 0 ? ' is-empty' : ''}`}
        >
          {formatReward(props.chore.rewardMinorUnits)}
        </span>
      </div>
      <div class="chore-management-card-detail">
        <Show when={props.chore.choreDescription}>
          {(d) => <p class="chore-management-description">{d()}</p>}
        </Show>
        <div class="chore-management-status">
          {/* US-UI-CHORE-REJECTIONS -- red status chip + inline reason
              when the chore's most-recent completion was rejected.
              Cleared implicitly by re-claiming via the Mark done
              button below (next list refresh drops the styling). */}
          <Show when={props.chore.lastRejectedAtUtc}>
            <span class="history-chip chore-row-rejected-chip">Rejected -- try again</span>
          </Show>
        </div>
        <Show when={props.chore.lastRejectionReason}>
          {(reason) => (
            <div
              class="chore-row-rejected-reason"
              data-testid={`chore-row-${props.chore.choreId}-rejection-reason`}
            >
              <strong>Why:</strong> {reason()}
            </div>
          )}
        </Show>
        <div class="chore-management-actions">
          <ReportContentButton
            context={{
              contentKind: 'chore',
              contentLocalId: `chore:${props.chore.choreId}`,
              reportedLocalAccountId: props.chore.createdByAccountId,
              label: `chore “${props.chore.choreName}”`,
              contentSnapshot: {
                choreId: props.chore.choreId,
                choreName: props.chore.choreName,
                choreDescription: props.chore.choreDescription,
                choreTypeLabel: props.chore.choreTypeLabel,
                zoneName: props.chore.zoneName,
                recurrencePattern: props.chore.recurrencePattern,
                childAccountId: props.chore.childAccountId,
                childDisplayName: props.chore.childDisplayName ?? null,
                createdByAccountId: props.chore.createdByAccountId,
                lastRejectionReason: props.chore.lastRejectionReason,
              },
            }}
          />
          <Show when={props.canClaim}>
            <Show
              when={!props.chore.hasOpenClaim}
              fallback={
                <span class="history-chip" style="background: #fff4cd; color: #8a6d00;">
                  Waiting on approval
                </span>
              }
            >
              {/* US-CHORE-10 (10b): when the chore is rejected, the
                  primary action becomes "Re-submit" instead of "Mark
                  done". The backend treats both as putting the
                  completion back into 'claimed' status -- but
                  re-submit acts on the existing rejected row (no new
                  row created), preserving the audit trail of the
                  rejected->claimed transition via the
                  chore_completion.resubmitted audit event. */}
              <Show
                when={props.chore.lastRejectedAtUtc}
                fallback={
                  <button
                    type="button"
                    class="bucket-tile-spend"
                    disabled={props.busyClaiming}
                    onClick={(event) =>
                      props.onMarkDone(props.chore, event.currentTarget.getBoundingClientRect())
                    }
                  >
                    {props.busyClaiming ? 'Marking…' : 'Mark done'}
                  </button>
                }
              >
                <button
                  type="button"
                  class="bucket-tile-spend"
                  disabled={props.busyResubmitting}
                  onClick={() => props.onResubmit(props.chore)}
                  data-testid={`chore-row-${props.chore.choreId}-resubmit`}
                >
                  {props.busyResubmitting ? 'Re-submitting…' : 'Re-submit'}
                </button>
              </Show>
            </Show>
          </Show>
          <Show when={props.canEdit}>
            <button
              type="button"
              class="ghost"
              disabled={props.busyChore}
              onClick={() => props.onEdit?.(props.chore)}
              data-testid={`chore-row-${props.chore.choreId}-edit`}
            >
              Edit
            </button>
          </Show>
          <Show when={props.canDeactivate}>
            <button
              type="button"
              class="ghost"
              disabled={props.busyChore}
              onClick={() => props.onDeactivate(props.chore)}
            >
              {props.busyChore ? 'Deactivating…' : 'Deactivate'}
            </button>
          </Show>
          <Show when={props.canReactivate}>
            <button
              type="button"
              class="ghost"
              disabled={props.busyChore}
              onClick={() => props.onReactivate(props.chore)}
            >
              {props.busyChore ? 'Reactivating…' : 'Reactivate'}
            </button>
          </Show>
        </div>
      </div>
    </li>
  );
};
